import extractValues from '../utils/extract-values.js'

/**
 * Escapes special RegExp characters in a string to make it safe for use in regex patterns
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for RegExp
 */
function escapeRegExp(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Highlights search terms in text with HTML mark tags
 * @param {string} text - Text to highlight
 * @param {string} searchTerm - Term to highlight (optional, uses current search if not provided)
 * @returns {string} HTML string with highlighted terms
 */
export function highlightText(text, searchTerm = '') {
  const source = text !== null ? String(text) : ''

  if (!searchTerm) return source

  const trimmedTerm = searchTerm.trim()

  if (!trimmedTerm) return source

  try {
    const re = new RegExp(escapeRegExp(trimmedTerm), 'gi') // global and case-insensitive
    return source.replace(re, (match) => `<mark>${match}</mark>`)
  } catch (e) {
    return source
  }
}

/**
 * Searches a single item to determine if it matches the search term and preprocessors.
 * @param {Object} item - The item to search.
 * @param {String} searchTerm - The search term to use (optional, uses current search if not provided).
 * @param {Array} preprocessors - Transformations that will add additional values to the search (optional)
 * @returns {boolean} Whether the item matches the search criteria.
 */
export function searchItem(item, searchTerm = '', preprocessors = []) {
  if (!searchTerm) return true

  const term = searchTerm.trim().toLowerCase()
  const values = extractValues(item, true) // unique values only
  const extraValues = preprocessors.map((fn) => fn(item)).filter((v) => v)
  const allValues = [...values, ...extraValues]

  return allValues.some((value) => String(value).toLowerCase().includes(term))
}

export function searchItems(items, searchTerm = '', preprocessors = []) {
  if (!items?.length) return items

  return items.filter((item) => searchItem(item, searchTerm, preprocessors))
}
